import {
  AssignmentTurnedIn,
  ContentCopy,
  Delete,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  Collapse,
  Fab,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import React, { useState } from 'react';

import { LightTooltip } from '../../../../../../../../../lib/designSystem/WhiteTooltip';
import { isDateAWeekDay } from '../../../../../../../../../lib/functions/dates';
import {
  formatDate,
  formatMoney,
} from '../../../../../../../../../lib/functions/utils';

const useStyles = makeStyles(() =>
  createStyles({
    table: {
      width: '100%',
      minWidth: '1200px',
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
    tableCell: {
      padding: '12px 8px !important',
      overflow: 'hidden',
      minWidth: '80px',
      maxWidth: '150px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableCellFolio: {
      padding: '12px 4px !important',
      overflow: 'hidden',
      minWidth: '60px',
      maxWidth: '80px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableCellCost: {
      padding: '12px 4px !important',
      overflow: 'hidden',
      minWidth: '80px',
      maxWidth: '100px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableCellTasa: {
      padding: '12px 8px !important',
      overflow: 'hidden',
      minWidth: '80px',
      maxWidth: '100px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      textAlign: 'right',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableCellNames: {
      padding: '12px 8px !important',
      overflow: 'hidden',
      minWidth: '120px',
      maxWidth: '160px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableContainer: {
      border: '1px solid #e8e8e8',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      overflow: 'auto',
      maxHeight: '600px',
      overflowY: 'auto',
      overflowX: 'auto',
      width: '100%',
    },
    tableBody: {
      backgroundColor: '#ffffff',
    },
    tableHead: {
      backgroundColor: '#f8f9fa !important',
      '& th': {
        backgroundColor: '#f8f9fa !important',
        color: '#495057 !important',
        fontWeight: '600 !important',
        fontSize: '13px !important',
        padding: '16px 8px !important',
        borderBottom: '2px solid #dee2e6',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      },
    },
    textField: {
      fontSize: '13px !important',
      '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        '& fieldset': {
          borderColor: '#e0e0e0',
        },
        '&:hover fieldset': {
          borderColor: '#1976d2',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#1976d2',
        },
      },
      '& .MuiInputBase-input': {
        padding: '8px 12px',
        fontSize: '13px',
      },
    },
    iconButton: {
      padding: '4px',
      margin: '0 1px',
      borderRadius: '4px',
      '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.08)',
      },
    },
    deleteButton: {
      color: '#dc3545',
      '&:hover': {
        backgroundColor: 'rgba(220, 53, 69, 0.08)',
      },
    },
    assignButton: {
      color: '#28a745',
      '&:hover': {
        backgroundColor: 'rgba(40, 167, 69, 0.08)',
      },
    },
    tableRow: {
      '&:hover': {
        backgroundColor: '#f8f9fa',
        transition: 'background-color 0.2s ease',
      },
      '&:nth-of-type(even)': {
        backgroundColor: '#fafbfc',
      },
      '&:nth-of-type(even):hover': {
        backgroundColor: '#f1f3f4',
      },
    },
    amountCell: {
      fontWeight: '600',
      color: '#2c3e50',
    },
    daysCell: {
      fontWeight: '500',
      color: '#6c757d',
    },
    rateCostCell: {
      fontWeight: '600',
      color: '#28a745',
    },
    actionsCell: {
      minWidth: '60px',
      maxWidth: '80px',
      padding: '6px !important',
    },
    chip: {
      fontSize: '11px',
      height: '20px',
      borderRadius: '10px',
    },
    '@media (max-width: 1200px)': {
      tableCell: {
        fontSize: '12px !important',
        padding: '8px 4px !important',
        maxWidth: '150px',
      },
      tableCellFolio: {
        fontSize: '12px !important',
        padding: '6px 2px !important',
        maxWidth: '60px',
      },
      tableCellNames: {
        fontSize: '12px !important',
        padding: '8px 4px !important',
        maxWidth: '100px',
      },
      tableCellTasa: {
        fontSize: '12px !important',
        padding: '8px 4px !important',
        maxWidth: '60px',
      },
      tableCellCost: {
        fontSize: '12px !important',
        padding: '6px 2px !important',
        maxWidth: '80px',
      },
    },
  }),
);

import { ExtensionDocument } from '../../types';

interface ExtensionsTableProps {
  extensions: ExtensionDocument[];
  setExtensions: (extensions: ExtensionDocument[]) => void;
  fixedArrearRate: number | null;
  noIvaComision: number | null;
  canChangeArrearRate: boolean;
}

const ExtensionsTable = (props: ExtensionsTableProps) => {
  const {
    extensions,
    setExtensions,
    fixedArrearRate,
    noIvaComision,
    canChangeArrearRate,
  } = props;
  const classes = useStyles();
  const [expandedRows, setExpandedRows] = useState<number[]>([0]);

  React.useEffect(() => {
    if (fixedArrearRate !== null && extensions.length > 0) {
      const newExtensions = extensions.map((doc: any) => {
        const days = calculateDays(
          new Date(doc.paymentDate),
          new Date(doc.dueDate),
        );
        const rateCost = calculateRateCost(
          doc.documentRemainingAmountNet,
          fixedArrearRate,
          days,
        );
        return {
          ...doc,
          rate: fixedArrearRate.toString(),
          rateCost,
        };
      });
      setExtensions(newExtensions);
    }
  }, [fixedArrearRate]);

  const toggleRowExpansion = (rowIndex: number) => {
    setExpandedRows((prev) =>
      prev.includes(rowIndex)
        ? prev.filter((index) => index !== rowIndex)
        : [...prev, rowIndex],
    );
  };

  const handleChangeObservation = (value: string, rowIndex: number) => {
    const newExtensions = extensions.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          observation: value,
        };
      }
      return doc;
    });
    setExtensions(newExtensions);
  };

  const copyFirstObservationToAll = () => {
    if (extensions.length === 0) return;

    const firstObservation = extensions[0].observation || '';
    const newExtensions = extensions.map((doc: any, i: any) => {
      if (i === 0) {
        return doc;
      }
      return {
        ...doc,
        observation: firstObservation,
      };
    });
    setExtensions(newExtensions);
  };

  const calculateDays = (start: Date, end: Date) => {
    const days = Math.floor(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
    );
    return days;
  };

  const calculateRateCost = (amount: number, rate: number, days: number) => {
    return Math.round((amount * rate * days) / 30 / 100);
  };

  const dateValidators = (date: Date, rowIndex: number) => {
    if (!isDateAWeekDay(date)) {
      alert('La fecha de vencimiento debe ser un día hábil');
      returnTempDateToActualDate(rowIndex);
      return false;
    }
    const startingPaymentDate = new Date(extensions[rowIndex].paymentDate);

    const days = calculateDays(startingPaymentDate, date);

    if (days <= 0) {
      alert(
        'La fecha de vencimiento no puede ser menor a la fecha de liberación',
      );
      returnTempDateToActualDate(rowIndex);
      return false;
    }
    return days;
  };

  const returnTempDateToActualDate = (rowIndex: number) => {
    const newExtensions = extensions.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          tempDueDate: doc.dueDate,
        };
      }
      return doc;
    });
    setExtensions(newExtensions);
  };

  const handleChangeTempDate = (e: any, rowIndex: number) => {
    const newExtensions = extensions.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          tempDueDate: e.target.value,
        };
      }
      return doc;
    });
    setExtensions(newExtensions);
  };

  const handleChangeDate = (value: any, rowIndex: number) => {
    const days = dateValidators(new Date(value), rowIndex);
    if (!days) return;
    const newExtensions = extensions.map((doc: any, i: any) => {
      if (i === rowIndex) {
        const rateCost = calculateRateCost(
          doc.documentRemainingAmountNet,
          parseFloat(doc.rate),
          days,
        );
        return {
          ...doc,
          dueDate: value,
          days,
          rateCost,
        };
      }
      return doc;
    });
    setExtensions(newExtensions);
  };

  const handleChangeRate = (e: any, rowIndex: number) => {
    if (fixedArrearRate !== null) {
      return;
    }

    const value = e.target.value;
    const currentRate = extensions[rowIndex].rate;

    if (value === '') {
      const newExtensions = extensions.map((doc: any, i: any) => {
        if (i === rowIndex) {
          return {
            ...doc,
            rate: '0',
            rateCost: 0,
          };
        }
        return doc;
      });
      setExtensions(newExtensions);
      return;
    }

    let finalValue = value;
    if (currentRate === '0') {
      finalValue = value;
    }

    const isValidFormat = /^\d*\.?\d*$/.test(finalValue);
    if (!isValidFormat) return;
    if (finalValue.endsWith('.')) {
      const newExtensions = extensions.map((doc: any, i: any) => {
        if (i === rowIndex) {
          const rateCost = calculateRateCost(
            doc.documentRemainingAmountNet,
            parseFloat(finalValue),
            doc.days,
          );
          return {
            ...doc,
            rate: finalValue,
            rateCost,
          };
        }
        return doc;
      });
      setExtensions(newExtensions);
      return;
    }

    const numbered = parseFloat(finalValue);

    if (isNaN(numbered) || numbered < 0 || numbered > 100) return;

    const newExtensions = extensions.map((doc: any, i: any) => {
      if (i === rowIndex) {
        const rateCost = calculateRateCost(
          doc.documentRemainingAmountNet,
          numbered,
          doc.days,
        );
        return {
          ...doc,
          rate: finalValue,
          rateCost,
        };
      }
      return doc;
    });

    setExtensions(newExtensions);
  };

  const getDisplayValue = (rate: any) => {
    if (fixedArrearRate !== null) {
      return fixedArrearRate.toString();
    }

    if (!rate || rate === '') {
      return '0';
    }
    return rate.toString();
  };

  const handleChangeOperationCostFixed = (e: any, rowIndex: number) => {
    const value = e.target.value.split('.').join('').split('$').join('');
    if (isNaN(value)) return;
    if (value < 0) return;
    const newExtensions = extensions.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          operationCostFixed: Number(value),
        };
      }
      return doc;
    });
    setExtensions(newExtensions);
  };

  const handleChangeOperationCostVariable = (e: any, rowIndex: number) => {
    const value = e.target.value;
    if (isNaN(value)) return;
    const numbered = parseFloat(value);
    if (numbered > 100 || numbered < 0) return;

    const newExtensions = extensions.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          operationCostVariable:
            (doc.documentRemainingAmountNet * numbered) / 100,
          operationCostRateVariable: value,
        };
      }
      return doc;
    });
    setExtensions(newExtensions);
  };

  const assignConditionsForAllExtensions = () => {
    if (extensions.length === 0) return;

    const newExtensions = extensions.map((doc: any, i: any) => {
      if (i === 0) {
        return doc;
      }

      const firstRow = extensions[0];
      const rate = parseFloat(firstRow.rate);
      const operationCostFixed = Number(firstRow.operationCostFixed);
      const operationCostRateVariable = parseFloat(
        firstRow.operationCostRateVariable,
      );
      const dueDate = firstRow.dueDate;

      const days = calculateDays(new Date(doc.paymentDate), new Date(dueDate));

      const rateCost = calculateRateCost(
        doc.documentRemainingAmountNet,
        rate,
        days,
      );

      const operationCostVariable =
        operationCostRateVariable > 0
          ? (doc.documentRemainingAmountNet * operationCostRateVariable) / 100
          : 0;

      return {
        ...doc,
        rate: firstRow.rate,
        rateCost,
        dueDate,
        tempDueDate: dueDate,
        operationCostFixed,
        operationCostVariable,
        operationCostRateVariable: firstRow.operationCostRateVariable,
        days,
      };
    });
    setExtensions([
      ...newExtensions.map((extension: any, index: any) => ({
        ...extension,
        key: index,
      })),
    ]);
  };

  const assignRateToAllExtensions = () => {
    if (extensions.length === 0) return;

    const rateToApply =
      fixedArrearRate !== null
        ? fixedArrearRate.toString()
        : extensions[0].rate;

    const newExtensions = extensions.map((doc: any, i: any) => {
      if (i === 0) {
        return doc;
      }
      const rateCost = calculateRateCost(
        doc.documentRemainingAmountNet,
        parseFloat(rateToApply),
        doc.days,
      );
      return {
        ...doc,
        rate: rateToApply,
        rateCost,
      };
    });

    setExtensions([
      ...newExtensions.map((extension: any, index: any) => ({
        ...extension,
        key: index,
      })),
    ]);
  };

  const deleteRow = (rowIndex: number) => {
    const newExtensions = extensions.filter(
      (doc: any, i: any) => i !== rowIndex,
    );
    setExtensions(newExtensions);
  };

  return (
    <TableContainer className={classes.tableContainer}>
      <Table className={classes.table}>
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell className={classes.actionsCell}></TableCell>
            <TableCell className={classes.tableCellFolio}>Folio</TableCell>
            <TableCell className={classes.tableCellNames}>
              Deudor / Cliente
            </TableCell>
            <TableCell className={classes.tableCell}>Saldos</TableCell>
            <TableCell className={classes.tableCell}>Liberación</TableCell>
            <TableCell className={classes.tableCellTasa}>
              <LightTooltip
                placement="top"
                title={
                  <Typography style={{ padding: 8 }}>
                    {fixedArrearRate !== null
                      ? `Tasa fija configurada: ${fixedArrearRate}%`
                      : 'Hacer clic para asignar la tasa del primer elemento a todos'}
                  </Typography>
                }
              >
                <Box
                  onClick={assignRateToAllExtensions}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    '&:hover': {
                      color: '#1976d2',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Tasa (%)
                </Box>
              </LightTooltip>
            </TableCell>
            <TableCell className={classes.tableCellCost}>Costo fijo</TableCell>
            <TableCell className={classes.tableCellCost}>
              Costo var. (%)
            </TableCell>
            <TableCell className={classes.tableCell}>
              Fecha vencimiento
            </TableCell>
            <TableCell className={classes.tableCell}>Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {extensions.map((row: any, rowIndex: number) => (
            <React.Fragment key={rowIndex}>
              <TableRow className={classes.tableRow}>
                <TableCell className={classes.actionsCell}>
                  <Box display="flex" justifyContent="start">
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => toggleRowExpansion(rowIndex)}
                      size="small"
                    >
                      {expandedRows.includes(rowIndex) ? (
                        <ExpandLess fontSize="small" />
                      ) : (
                        <ExpandMore fontSize="small" />
                      )}
                    </IconButton>
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => deleteRow(rowIndex)}
                      size="small"
                    >
                      <Delete
                        className={classes.deleteButton}
                        fontSize="small"
                      />
                    </IconButton>
                    {rowIndex === 0 && (
                      <LightTooltip
                        placement="right"
                        title={
                          <Typography style={{ padding: 12 }}>
                            Asignar mismas condiciones a todas
                          </Typography>
                        }
                      >
                        <IconButton
                          className={classes.iconButton}
                          onClick={() => assignConditionsForAllExtensions()}
                          size="small"
                        >
                          <AssignmentTurnedIn
                            className={classes.assignButton}
                            fontSize="small"
                          />
                        </IconButton>
                      </LightTooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell className={classes.tableCellFolio}>
                  {row.folio}
                </TableCell>
                <TableCell className={classes.tableCellNames}>
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                      }}
                    >
                      {row.stakeholderName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                      }}
                    >
                      {row.businessName}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Stack spacing={0.5} alignItems="center">
                    <Box className={classes.amountCell} textAlign="center">
                      <Chip
                        label={`Monto: ${formatMoney(row.amount)}`}
                        className={classes.chip}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ fontSize: '10px', height: '18px' }}
                      />
                    </Box>
                    <Box className={classes.amountCell} textAlign="center">
                      <Chip
                        label={`Deuda: ${formatMoney(row.documentRemainingAmountNet)}`}
                        className={classes.chip}
                        color="error"
                        variant="outlined"
                        size="small"
                        sx={{ fontSize: '10px', height: '18px' }}
                      />
                    </Box>

                    <Box className={classes.amountCell} textAlign="center">
                      <Chip
                        label={`Atraso: ${formatMoney(row.totalArrear)}`}
                        className={classes.chip}
                        color="warning"
                        variant="outlined"
                        size="small"
                        sx={{ fontSize: '10px', height: '18px' }}
                      />
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {formatDate(row.paymentDate)}
                </TableCell>
                <TableCell className={classes.tableCellTasa}>
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      value={getDisplayValue(row.rate)}
                      onChange={(e) => handleChangeRate(e, rowIndex)}
                      onFocus={(e) => {
                        if (e.target.value === '0') {
                          e.target.select();
                        }
                      }}
                      disabled={
                        !canChangeArrearRate || fixedArrearRate !== null
                      }
                      InputProps={{
                        classes: { input: classes.textField },
                        ...(fixedArrearRate !== null && {
                          style: {
                            backgroundColor: '#f5f5f5',
                            color: '#666',
                            cursor: 'not-allowed',
                          },
                        }),
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #e0e0e0',
                        borderRadius: '12px',
                        padding: '2px 8px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        color: '#666',
                        zIndex: 1,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      }}
                    >
                      {formatMoney(row.rateCost)}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell className={classes.tableCellCost}>
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      value={formatMoney(row.operationCostFixed)}
                      onChange={(e) =>
                        handleChangeOperationCostFixed(e, rowIndex)
                      }
                      InputProps={{ classes: { input: classes.textField } }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #e0e0e0',
                        borderRadius: '12px',
                        padding: '2px 8px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        color: '#666',
                        zIndex: 1,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      }}
                    >
                      {formatMoney(
                        row.operationCostFixed * (noIvaComision ? 1 : 1.19),
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell className={classes.tableCellCost}>
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      value={row.operationCostRateVariable}
                      onChange={(e) =>
                        handleChangeOperationCostVariable(e, rowIndex)
                      }
                      InputProps={{ classes: { input: classes.textField } }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #e0e0e0',
                        borderRadius: '12px',
                        padding: '2px 8px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        color: '#666',
                        zIndex: 1,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      }}
                    >
                      {formatMoney(
                        row.operationCostVariable * (noIvaComision ? 1 : 1.19),
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      value={row.tempDueDate || row.dueDate}
                      type="date"
                      onChange={(e) => handleChangeTempDate(e, rowIndex)}
                      onBlur={() =>
                        handleChangeDate(
                          extensions[rowIndex].tempDueDate ||
                            extensions[rowIndex].dueDate,
                          rowIndex,
                        )
                      }
                      InputProps={{ classes: { input: classes.textField } }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: '30px',
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #e0e0e0',
                        borderRadius: '12px',
                        padding: '2px 8px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        color: '#666',
                        zIndex: 1,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      }}
                    >
                      {row.days || 0} días
                    </Box>
                  </Box>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {formatMoney(
                    (row.operationCostVariable * (noIvaComision ? 1 : 1.19) ||
                      0) +
                      (row.operationCostFixed * (noIvaComision ? 1 : 1.19) ||
                        0) +
                      (row.rateCost || 0),
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell
                  style={{ paddingBottom: 0, paddingTop: 0 }}
                  colSpan={10}
                >
                  <Collapse
                    in={expandedRows.includes(rowIndex)}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box sx={{ margin: 1, position: 'relative' }}>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        margin="8px 0px"
                      >
                        Observaciones
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={row.observation || ''}
                        onChange={(e) =>
                          handleChangeObservation(e.target.value, rowIndex)
                        }
                        placeholder="Ingresar observaciones para esta prórroga..."
                        variant="outlined"
                        size="small"
                      />
                      {rowIndex === 0 && extensions.length > 1 && (
                        <LightTooltip
                          placement="top"
                          title={
                            <Typography style={{ padding: 8 }}>
                              Copiar esta observación a todas las demás
                              prórrogas
                            </Typography>
                          }
                        >
                          <Fab
                            size="small"
                            color="primary"
                            onClick={copyFirstObservationToAll}
                            sx={{
                              position: 'absolute',
                              bottom: 6,
                              right: 6,
                              width: 24,
                              height: 24,
                              minWidth: 24,
                              minHeight: 24,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                                transform: 'scale(1.05)',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <ContentCopy sx={{ fontSize: '14px' }} />
                          </Fab>
                        </LightTooltip>
                      )}
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ExtensionsTable;
